<template>
  <ValidationObserver
    ref="applicationSharingRules"
  >
    <b-modal
      id="application-sharing-modal"
      ref="application-sharing-modal"
      centered
      title="Application Sharing"
      no-close-on-backdrop
      @hidden="closeModal"
      @show="setModalData"
    >
      <b-overlay
        id="overlay-background"
        :show="isLoading"
        variant="white"
        opacity="1"
        rounded="sm"
      >
        <b-row>
          <b-col
            sm="12"
            class="mb-1"
          >
            <b-form-group
              label="Access Settings"
            >
              <validation-provider
                #default="{ errors }"
                name="Access Settings"
                rules="required"
              >
                <v-select
                  v-model="applicationSharingSettings.type"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="accessSettingsOptions"
                  :clearable="false"
                  :reduce="val => val.value"
                  :placeholder="'Select'"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col
            v-if="applicationSharingSettings.type == 'only_selected_accounts'"
            sm="12"
            class="mb-1"
          >
            <b-form-group
              label="Accounts with Access"
            >
              <validation-provider
                #default="{ errors }"
                name="Accounts with Access"
                rules="required"
              >
                <v-select
                  v-model="applicationSharingSettings.selectedAccounts"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="accountsList"
                  :clearable="false"
                  label="full_name"
                  :reduce="account => account.id"
                  :placeholder="accountsFieldStatus"
                  :close-on-select="false"
                  :deselect-from-dropdown="true"
                  multiple
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
      </b-overlay>

      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-primary"
            @click="copyLink"
          >
            Copy Link
          </b-button>
          <b-button
            variant="primary"
            class="float-right"
            :disabled="isLoading"
            @click="handleForm"
          >
            Done
          </b-button>
        </div>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BFormGroup,
  BOverlay,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useApplicationSharingModal from '@/views/components/application-sharing-modal/useApplicationSharingModal'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BButton,
    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    applicationId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      applicationSharingSettings: {
        type: null,
        selectedAccounts: [],
      },
      accessSettingsOptions: [
        {
          label: 'Anyone with the link',
          value: 'enable_with_link',
        },
        {
          label: 'Only Accounts with access',
          value: 'only_selected_accounts',
        },
      ],
      accountsList: [],
      accountsFieldStatus: 'Loading...',
      applicationSharingLink: '',
      required,
    }
  },
  computed: {
    programId() {
      return this.$store.getters['verticalMenu/getDefaultProgram']
    },
  },
  setup() {
    const {
      fetchApplicationSharingSettings,
      updateApplicationSharingSettings,
      fetchApplicationSharingLink,
      fetchUsers,
    } = useApplicationSharingModal()

    return {
      fetchApplicationSharingSettings,
      updateApplicationSharingSettings,
      fetchApplicationSharingLink,
      fetchUsers,
    }
  },
  methods: {
    closeModal() {
      this.applicationSharingSettings = {
        type: null,
        selectedAccounts: [],
      }
      this.accountsList = []
      this.accountsFieldStatus = 'Loading...'
      this.applicationSharingLink = null
      this.$refs['application-sharing-modal'].hide()
    },
    async setModalData() {
      try {
        this.isLoading = true
        await this.setUsersList()
        await this.setApplicationSharingSettings()
        await this.setApplicationSharingLink()
      } finally {
        this.isLoading = false
      }
    },
    async setUsersList() {
      const queryParams = {
        programId: this.programId,
        // role: ['parent', 'child'],
        // applicationId: this.applicationId,
      }
      this.accountsList = await this.fetchUsers(queryParams)
      this.accountsFieldStatus = 'Select'
    },
    async setApplicationSharingSettings() {
      this.applicationSharingSettings = await this.fetchApplicationSharingSettings(this.applicationId)
    },
    async setApplicationSharingLink() {
      this.applicationSharingLink = await this.fetchApplicationSharingLink(this.applicationId)
    },
    handleForm() {
      // eslint-disable-next-line consistent-return
      return new Promise((resolve, reject) => {
        this.$refs.applicationSharingRules.validate().then(success => {
          if (success) {
            const queryParams = {
              sharingSettings: this.applicationSharingSettings,
            }
            this.updateApplicationSharingSettings(this.applicationId, queryParams).then(() => {
              this.$refs['application-sharing-modal'].hide()
            })
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    copyLink() {
      if (this.applicationSharingLink) {
        navigator.clipboard.writeText(this.applicationSharingLink).then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Link copied',
              icon: 'BellIcon',
            },
          })
        })
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Can not copy! The link is empty',
            variant: 'danger',
            icon: 'BellIcon',
          },
        })
      }
    },
  },
}
</script>
